import React from 'react';
import { Form } from 'antd';

import { DatePicker, LabeledInput, LabeledTextArea, Uploader } from '../components/common';
import {
  SearchDrugsDropdown,
  SearchStakeholdersDropdown,
  StakeholderTypeDropdown,
  SearchEstablishmentDropdown,
  OperationDropdown,
  CitiesDropDown,
  RegionsDropdown,
  CountriesDropdown,
  SearchLabelLevelTypesDropdown,
  SearchLabelNameDropdown,
  SearchStakeholdersBySubscriptionDropdown } from '../components/common/SearchDropdowns';
import i18n from '../i18n';
import { CustomCheckBox } from '../components/common/CheckboxGroup/CustomCheckBox';
import { RecallLevelSelect } from '../components/common/SearchDropdowns/RecallLevel';
import { RecallUnitLevel } from '../components/common/SearchDropdowns/RecallUnitLevel';
import { SubscriptionPackageDropdown } from '../components/common/SearchDropdowns/SubscriptionPackageDropdown';
import { SubscriptionTypeDropdown } from '../components/common/SearchDropdowns/SubscriptionTypeDropdown';
import ActivePassiveSelect from '../components/common/StaticSelect/ActivePassiveSelect';
import DrugRequestStatusSelect from '../components/common/StaticSelect/DrugRequestStatusSelect';
import UnregisteredDrugRequestTypeSelect from '../components/common/StaticSelect/UnregisteredDrugRequestTypeSelect';
import { formFields } from '../constants';
import DomainsSelect from '../components/common/StaticSelect/DomainsSelect';
import YesNoSelect from '../components/common/StaticSelect/YesNoSelect';
import LagalStatus from '../components/common/StaticSelect/LagalStatus';
import AuthorizationStatus from '../components/common/StaticSelect/AuthorizationStatus';
import MarketingStatus from '../components/common/StaticSelect/MarketingStatus';
import StorageConditionsSelect from '../components/common/StaticSelect/StorageConditionsSelect';
import StrengthValueUnitSelect from '../components/common/StaticSelect/StrengthValueUnitSelect';
import UnitOfVolumeSelect from '../components/common/StaticSelect/UnitOfVolumeSelect';
import { DrugGenericNameDropdown } from '../components/common/SearchDropdowns/DrugGenericNameDropdown';
import { GenericNameDdlOrText } from '../components/common/SearchDropdowns/GenericNameDdlOrText';
import ControlLevelSelect from '../components/common/StaticSelect/ControlLevelSelect';

import { FIELD_TYPE } from './CONSTANT';


export const generateField = ({ id, label, formik, onChange, fieldType, ...p }) => {

  /**
 * Generates a form field component based on the provided parameters.
 *
 * @param {Object} props - The props object containing the field properties.
 * @param {string} props.id - The unique identifier for the form field.
 * @param {string} props.label - The label for the form field.
 * @param {Object} props.formik - The Formik object for form management.
 * @param {boolean} props.disabled - Indicates if the field is disabled.
 * @param {Function} props.onChange - The function to handle field value changes.
 * @param {string} props.fieldType - The type of the form field.
 * @returns {JSX.Element} A Form.Item component with a LabeledInput for the field.
 */
  const commonProps = {
    ...p,
    label: label ?? i18n.t(`form.${id}`),
    withAsterisks: formik.values[`${id}_Required`],
    onChange: e => (onChange ? onChange(e) : formik.setFieldValue(id, e)),
    disabled: formik.values[`${id}_Disabled`]
  };

  const [ Component, initialProps ] = {
    [FIELD_TYPE.STRING]: [
      LabeledInput,
      {
        ...commonProps,
        onChange: onChange ?? formik.handleChange
      }
    ],
    [FIELD_TYPE.NUMBER]: [
      LabeledInput,
      {
        ...commonProps,
        type: 'number',
        onChange: onChange ?? formik.handleChange
      }
    ],
    [FIELD_TYPE.DDL_STAKEHOLDER]: [
      SearchStakeholdersDropdown,
      {
        ...commonProps
      }
    ],
    [FIELD_TYPE.DDL_DRUG]: [
      SearchDrugsDropdown,
      {
        ...commonProps
      }
    ],
    [FIELD_TYPE.DDL_STAKEHOLDER_TYPE]: [
      StakeholderTypeDropdown,
      {
        ...commonProps
      }
    ],
    [FIELD_TYPE.DDL_ESTABLISHMENT]: [
      SearchEstablishmentDropdown,
      {
        ...commonProps
      }
    ],
    [FIELD_TYPE.DDL_STAKEHOLDER_TYPE]: [
      StakeholderTypeDropdown,
      {
        ...commonProps
      }
    ],
    [FIELD_TYPE.DATE_PICKER]: [
      DatePicker,
      {
        ...commonProps
      }
    ],
    [FIELD_TYPE.CHECKBOX]: [
      CustomCheckBox,
      {
        ...commonProps,
        onChange: e => (onChange ? onChange(e) : formik.setFieldValue(id, e.target.checked))
      }
    ],
    [FIELD_TYPE.DDL_RECALL_PRODUCT_LEVEL]: [
      RecallUnitLevel,
      {
        ...commonProps
      }
    ],
    [FIELD_TYPE.DDL_RECALL_LEVEL]: [
      RecallLevelSelect,
      {
        ...commonProps
      }
    ],
    [FIELD_TYPE.DDL_OPERATION]: [
      OperationDropdown,
      {
        ...commonProps
      }
    ],
    [FIELD_TYPE.DDL_REGION]: [
      RegionsDropdown,
      {
        ...commonProps,
        onChange: e => {
          formik.setValues({
            ...formik.values,
            [id]: e,
            [formFields.CITY]: undefined
          });
        }
      }
    ],
    [FIELD_TYPE.DDL_CITY]: [
      CitiesDropDown,
      {
        ...commonProps,
        region: formik.values[formFields.REGION]
      }
    ],
    [FIELD_TYPE.DDL_SUBSCRIPTION_PACKAGE]: [
      SubscriptionPackageDropdown,
      {
        ...commonProps,
        subscriptionTypeId: (() => {
          if (p.fixedSubscriptionTypeId !== undefined) {
            return p.fixedSubscriptionTypeId;
          }
          if (p.showAllPackages) {
            return null;
          }

          return formik.values[formFields.SUBSCRIPTION_TYPE];
        })(),
        setValueAsObject: !p.multiple,
        onChange: e => {
          let value = e;
          if (p.multiple) {
            value = Array.isArray(e) ? e : [];
          } else if (typeof e === 'object' && e?.item) {
            value = e.item.id;
          } else if (typeof e === 'string' || typeof e === 'number') {
            value = e;
          }
          if (onChange) {
            onChange(value);
          } else {
            formik.setFieldValue(id, value);
          }
        }
      }
    ],
    [FIELD_TYPE.DDL_SUBSCRIPTION_TYPE]: [
      SubscriptionTypeDropdown,
      {
        ...commonProps
      }
    ],
    [FIELD_TYPE.DDL_STAKEHOLDER_BY_SUBSCRIPTION]: [
      SearchStakeholdersBySubscriptionDropdown,
      {
        ...commonProps,
        subscriptionTypeId: formik.values[formFields.SUBSCRIPTION_TYPE],
        subscriptionPackageId: formik.values[formFields.SUBSCRIPTION_PACKAGE],
        setValueAsObject: true,
        onChange: e => {
          let value = e;
          if (typeof e === 'object' && e?.item) {
            value = e.item.shId;
          } else if (typeof e === 'string') {
            value = parseInt(e, 10);
          }
          if (onChange) {
            onChange(value);
          } else {
            formik.setFieldValue(id, value);
          }
        }
      }
    ],
    [FIELD_TYPE.DDL_ACTIVE_PASSIVE_SELECT]: [
      ActivePassiveSelect,
      {
        ...commonProps
      }
    ],
    [FIELD_TYPE.DDL_DRUGS_REQUESTS_STATUS]: [
      DrugRequestStatusSelect,
      {
        ...commonProps
      }
    ],
    [FIELD_TYPE.DDL_UNREGISTERED_DRUG_REQUEST_TYPE]: [
      UnregisteredDrugRequestTypeSelect,
      {
        ...commonProps
      }
    ],
    [FIELD_TYPE.DDL_DOMAINS]: [
      DomainsSelect,
      {
        ...commonProps
      }
    ],
    [FIELD_TYPE.DDL_COUNTRY_OF_MANUFACTURER]: [
      CountriesDropdown,
      {
        ...commonProps
      }
    ],
    [FIELD_TYPE.DDL_YES_NO]: [
      YesNoSelect,
      {
        ...commonProps
      }
    ],
    [FIELD_TYPE.DDL_LAGAL_STATUS]: [
      LagalStatus,
      {
        ...commonProps
      }
    ],
    [FIELD_TYPE.DDL_CONTROL_LEVEL]: [
      ControlLevelSelect,
      {
        ...commonProps
      }
    ],
    [FIELD_TYPE.DDL_AUTHORIZATION_STATUS]: [
      AuthorizationStatus,
      {
        ...commonProps
      }
    ],
    [FIELD_TYPE.DDL_MARKETING_STATUS]: [
      MarketingStatus,
      {
        ...commonProps
      }
    ],
    [FIELD_TYPE.DDL_STORAGE_CONDITIONS]: [
      StorageConditionsSelect,
      {
        ...commonProps
      }
    ],
    [FIELD_TYPE.DDL_STRENGTH_VALUE_UNIT]: [
      StrengthValueUnitSelect,
      {
        ...commonProps
      }
    ],
    [FIELD_TYPE.DDL_UNIT_OF_VOLUME]: [
      UnitOfVolumeSelect,
      {
        ...commonProps
      }
    ],
    [FIELD_TYPE.LABELED_TEXT_AREA]: [
      LabeledTextArea,
      {
        ...commonProps,
        onChange: onChange ?? formik.handleChange
      }
    ],
    [FIELD_TYPE.DDL_DRUG_GENERIC_NAME]: [
      DrugGenericNameDropdown,
      {
        ...commonProps
      }
    ],
    [FIELD_TYPE.GENERIC_NAME_DDL_OR_TEXT]: [
      GenericNameDdlOrText,
      {
        ...commonProps
      }
    ],
    [FIELD_TYPE.FILES]: [
      Uploader,
      {
        ...commonProps
      }
    ],
    [FIELD_TYPE.DDL_LABEL_LEVEL_TYPES]: [
      SearchLabelLevelTypesDropdown,
      {
        ...commonProps
      }
    ],
    [FIELD_TYPE.DDL_LABEL_NAME]: [
      SearchLabelNameDropdown,
      {
        ...commonProps
      }
    ]

  }[fieldType];

  return (
    <Form.Item
      key={ id }
      validateStatus={ formik.touched[id] && formik.errors[id] ? 'error' : '' }
      help={
        formik.touched[id] && formik.errors[id]
        ? (
          <span data-testid={ `error-${id}` }>
            { formik.errors[id] }
          </span>
)
        : '' }
    >
      <Component
        { ...initialProps }
        id={ id }
        value={ formik.values[id] }
      />
    </Form.Item>
  );
};


